import React, { useEffect, useState, useCallback, useRef } from "react";
import { getDictByKind } from "../../../server/Category";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ProductsFilters from "./ProductsFilters";
import ProductsTable from "./ProductsTable";
import {
  setFilters,
  clearFilters,
  setBaseProducts,
  reload,
} from "../../../server/ProductsFilters";
import { useProductsFilters } from "./UseProductsFilters";
import "./ProductsWithFilters.scss";
import { getProductsByStatus } from "../../../server/Product";
import ListPagination from "../../containers/ListPagination";
import { useListPagination } from "../../../hooks/useListPagination";

const LIMIT = 4000;

const ProductsWithFilters = ({ openModal }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { stationId, reloed } = useSelector((state) => ({
    stationId: state.user.stationId,
    reloed: state.productFilters.reloed,
  }));
  const filters = useSelector((state) => state.productFilters.filters);
  const categoryChips = useSelector(
    (state) => state.productFilters.filters.categoryChips,
  );
  const [brand, setBrand] = useState([]);
  const [subBrand, setSubBrand] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [statusFilter, setStatusFilter] = useState([]);

  const baseProducts = useProductsFilters(stationId, statusFilter);

  useEffect(() => {
    getDictByKind("brand").then(setBrand);
    getDictByKind("sub-brand").then(setSubBrand);
  }, []);

  useEffect(() => {
    if (selectedProduct) {
      navigate(`/edit-product/${selectedProduct.productId}/information`, {
        state: { from: "/admin/products" },
      });
    }
  }, [selectedProduct, navigate]);

  const loadProducts = useCallback(
    async (pageToLoad, isFirstLoad = false) => {
      try {
        if (isFirstLoad) {
          dispatch(reload());
        } else {
          setLoadingMore(true);
        }
        const res = await getProductsByStatus(stationId, {}, pageToLoad, LIMIT);
        if (isFirstLoad) {
          dispatch(setBaseProducts(res));
        } else {
          dispatch(setBaseProducts([...(baseProducts || []), ...res]));
        }
        if (!res?.length || res.length < LIMIT) {
          setHasMore(false);
        }
      } finally {
        setLoadingMore(false);
      }
    },
    [stationId, dispatch, baseProducts],
  );

  useEffect(() => {
    if (
      filters.brandIds.length > 0 ||
      filters.subBrandIds.length > 0 ||
      filters.gtin ||
      categoryChips.length > 0 ||
      statusFilter.length > 0
    )
      return;
    dispatch(reload());
    setPage(1);
    setHasMore(true);
    loadProducts(1, true);
  }, [stationId, filters, statusFilter]);

  const filteredProducts = baseProducts?.filter((item) => {
    const p = item.product || item;
    const matchesSearch = p.name
      ?.toLowerCase()
      .includes(filters.search?.toLowerCase() ?? "");
    return matchesSearch;
  });

  const setProductsFromFilters = useCallback(
    (prods) => dispatch(setBaseProducts(prods)),
    [dispatch],
  );

  const handleClearFilters = useCallback(() => {
    dispatch(clearFilters());
    setStatusFilter([]);
  }, [dispatch]);

  const {
    toShow,
    pCounter,
    cFrom,
    cTo,
    pTo,
    isRowVisible,
    showPagination,
    showPaggination,
    UpPaggination,
    downPaggination,
    toShowFun,
  } = useListPagination(filteredProducts?.length ?? 0);

  const loadingMoreRef = useRef(false);
  const filteredCount = filteredProducts?.length ?? 0;
  const canLoadMoreFromApi =
    filters.brandIds.length === 0 &&
    filters.subBrandIds.length === 0 &&
    !filters.gtin &&
    categoryChips.length === 0 &&
    statusFilter.length === 0;

  useEffect(() => {
    if (
      !canLoadMoreFromApi ||
      loadingMore ||
      !hasMore ||
      !filteredCount ||
      pTo < filteredCount
    ) {
      return;
    }

    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;

    const nextPage = page + 1;
    setPage(nextPage);
    loadProducts(nextPage).finally(() => {
      loadingMoreRef.current = false;
    });
  }, [
    canLoadMoreFromApi,
    loadingMore,
    hasMore,
    filteredCount,
    pTo,
    page,
    loadProducts,
  ]);

  return (
    <div className="products-page">
      <ProductsFilters
        brand={brand}
        subBrand={subBrand}
        filters={filters}
        setFilters={(payload) => dispatch(setFilters(payload))}
        setProducts={setProductsFromFilters}
        clearFilters={handleClearFilters}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        filteredProducts={filteredProducts}
        openModal={openModal}
      />
      {reloed ? (
        <p>טוען...</p>
      ) : filteredProducts?.length ? (
        <>
          <ListPagination
            className="products-pagination"
            showPagination={showPagination}
            toShow={toShow}
            toShowFun={toShowFun}
            cFrom={cFrom}
            cTo={cTo}
            pCounter={pCounter}
            showPaggination={showPaggination}
            downPaggination={downPaggination}
            UpPaggination={UpPaggination}
          />
          <ProductsTable
            products={filteredProducts}
            onSelect={setSelectedProduct}
            isRowVisible={isRowVisible}
          />
          {loadingMore && (
            <div style={{ textAlign: "center", padding: "20px" }}>
              טוען עוד...
            </div>
          )}
        </>
      ) : (
        <p>לא נמצאו מוצרים עם הקריטריונים שנבחרו</p>
      )}
    </div>
  );
};

export default ProductsWithFilters;
